import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Row, Col, Card, Button, Spinner, Form } from "react-bootstrap";
import 'leaflet/dist/leaflet.css';
import 'leaflet/dist/images/marker-shadow.png';
// Import the rotated marker plugin
import 'leaflet-rotatedmarker';
import { 
  FaPlay, 
  FaPause, 
  FaStop, 
  FaSearch, 
  FaTachometerAlt,
  FaClock,
  FaMapMarkerAlt,
  FaBatteryThreeQuarters,
  FaBatteryFull,
  FaBatteryHalf,
  FaBatteryQuarter,
  FaBatteryEmpty,
  FaPlug,
  FaCheckCircle,
  FaPauseCircle,
  FaExclamationCircle,
  FaRoute,
  FaWifi,
  FaSignal,
  FaTruck,
  FaCar,
  FaFastForward,
} from 'react-icons/fa';

import { getDisplayDateTimeFormat } from '../util/generalUtills';
import { getTileLayerConfig, getDefaultMapView } from '../util/mapProvider';

// Get vehicle icon SVG based on vehicle type (same as LeafletMapAllVehicle)
const getVehicleIconSVG = (vehicleType, color) => {
  const carSVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" style="width: 32px; height: 32px;">
    <circle cx="50" cy="150" r="15" fill="${color}" opacity="0.8" stroke="#000" stroke-width="2"/>
    <circle cx="50" cy="150" r="10" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="50" cy="150" r="5" fill="#000"/>
    <circle cx="150" cy="150" r="15" fill="${color}" opacity="0.8" stroke="#000" stroke-width="2"/>
    <circle cx="150" cy="150" r="10" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="150" cy="150" r="5" fill="#000"/>
    <circle cx="50" cy="50" r="12" fill="${color}" opacity="0.8" stroke="#000" stroke-width="2"/>
    <circle cx="50" cy="50" r="8" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="50" cy="50" r="4" fill="#000"/>
    <circle cx="150" cy="50" r="12" fill="${color}" opacity="0.8" stroke="#000" stroke-width="2"/>
    <circle cx="150" cy="50" r="8" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="150" cy="50" r="4" fill="#000"/>
    <rect x="40" y="60" width="120" height="80" rx="8" fill="${color}" opacity="0.9" stroke="#000" stroke-width="2"/>
    <rect x="60" y="70" width="80" height="25" rx="3" fill="none" stroke="#000" stroke-width="1.5" opacity="0.4"/>
    <rect x="70" y="100" width="30" height="20" rx="2" fill="none" stroke="#000" stroke-width="1" opacity="0.3"/>
    <rect x="100" y="100" width="30" height="20" rx="2" fill="none" stroke="#000" stroke-width="1" opacity="0.3"/>
  </svg>`;
  
  const tractorSVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" style="width: 32px; height: 32px;">
    <circle cx="55" cy="155" r="22" fill="${color}" opacity="0.85" stroke="#000" stroke-width="2.5"/>
    <circle cx="55" cy="155" r="16" fill="none" stroke="#000" stroke-width="2"/>
    <circle cx="55" cy="155" r="10" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="55" cy="155" r="5" fill="#000"/>
    <circle cx="145" cy="155" r="22" fill="${color}" opacity="0.85" stroke="#000" stroke-width="2.5"/>
    <circle cx="145" cy="155" r="16" fill="none" stroke="#000" stroke-width="2"/>
    <circle cx="145" cy="155" r="10" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="145" cy="155" r="5" fill="#000"/>
    <circle cx="45" cy="50" r="14" fill="${color}" opacity="0.85" stroke="#000" stroke-width="2"/>
    <circle cx="45" cy="50" r="10" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="45" cy="50" r="5" fill="#000"/>
    <circle cx="155" cy="50" r="14" fill="${color}" opacity="0.85" stroke="#000" stroke-width="2"/>
    <circle cx="155" cy="50" r="10" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="155" cy="50" r="5" fill="#000"/>
    <ellipse cx="100" cy="45" rx="35" ry="18" fill="${color}" opacity="0.9" stroke="#000" stroke-width="2"/>
    <ellipse cx="100" cy="45" rx="28" ry="14" fill="none" stroke="#000" stroke-width="1.5" opacity="0.5"/>
    <rect x="70" y="75" width="60" height="45" rx="6" fill="${color}" opacity="0.95" stroke="#000" stroke-width="2.5"/>
    <rect x="75" y="80" width="50" height="20" rx="3" fill="none" stroke="#000" stroke-width="1.5" opacity="0.4"/>
    <rect x="78" y="83" width="44" height="14" rx="2" fill="rgba(135, 206, 250, 0.3)"/>
    <line x1="100" y1="100" x2="100" y2="120" stroke="#000" stroke-width="1.5" opacity="0.6"/>
    <rect x="85" y="120" width="30" height="25" rx="4" fill="${color}" opacity="0.8" stroke="#000" stroke-width="2"/>
    <circle cx="100" cy="148" r="4" fill="#000" opacity="0.7"/>
    <rect x="98" y="148" width="4" height="8" fill="#000" opacity="0.6"/>
    <rect x="90" y="35" width="20" height="12" rx="2" fill="none" stroke="#000" stroke-width="1.5" opacity="0.6"/>
    <rect x="75" y="70" width="4" height="8" rx="1" fill="#000" opacity="0.5"/>
  </svg>`;
  
  if (vehicleType?.toLowerCase() === 'tractor') {
    return tractorSVG;
  }
  return carSVG;
};

// Create dynamic icon with rotation
const createRotatedCarIcon = (heading, vehicleType, isCharging, speed) => {
  const headingValue = heading ? parseFloat(heading) : 0;
  const speedValue = parseFloat(speed) || 0;
  const speedColor = speedValue > 0 ? '#28a745' : '#dc3545';
  const iconSVG = getVehicleIconSVG(vehicleType, speedColor);
  const chargingBadge = isCharging ? '<div style="position: absolute; top: -3px; right: -3px; background: #28a745; border-radius: 50%; width: 14px; height: 14px; border: 2px solid white; z-index: 10; display: flex; align-items: center; justify-content: center; font-size: 8px; color: white;">⚡</div>' : '';
  
  return L.divIcon({
    className: "custom-div-icon",
    html: `<div style="position: relative; transform: rotate(${headingValue}deg); transform-origin: center center; display: inline-flex; align-items: center; justify-content: center;">
             <div style="position: absolute; width: 44px; height: 44px; border-radius: 50%; background-color: ${speedColor}; opacity: 0.2; z-index: 0;"></div>
             <div style="position: absolute; width: 40px; height: 40px; border-radius: 50%; border: 3px solid ${speedColor}; z-index: 1;"></div>
             <div style="position: relative; z-index: 2; display: flex; align-items: center; justify-content: center; filter: drop-shadow(0 0 3px ${speedColor});">
               ${iconSVG}
             </div>
             ${chargingBadge}
           </div>`,
    iconSize: [44, 44],
    iconAnchor: [22, 22],
  popupAnchor: [0, -20],
});
};

// Get battery icon
const getBatteryIcon = (level) => {
  const batteryLevel = parseFloat(level) || 0;
  if (batteryLevel >= 0.75) return <FaBatteryFull style={{ color: '#28a745' }} />;
  if (batteryLevel >= 0.5) return <FaBatteryThreeQuarters style={{ color: '#ffc107' }} />;
  if (batteryLevel >= 0.25) return <FaBatteryHalf style={{ color: '#fd7e14' }} />;
  if (batteryLevel > 0) return <FaBatteryQuarter style={{ color: '#dc3545' }} />;
  return <FaBatteryEmpty style={{ color: '#6c757d' }} />;
};

// Get activity icon
const getActivityIcon = (activityType) => {
  if (activityType?.toLowerCase() === 'still') {
    return <FaPauseCircle style={{ color: '#6c757d' }} />;
  }
  return <FaCheckCircle style={{ color: '#28a745' }} />;
};

// Helper component to fit map bounds based on route points only once (on load)
const FitMapBoundsOnLoad = ({ routePoints }) => {
  const map = useMap();
  const boundsFitRef = useRef(false); // Ref to ensure bounds are only fit once

  useEffect(() => {
    if (routePoints.length > 0 && !boundsFitRef.current) {
      const bounds = L.latLngBounds(routePoints);
      map.fitBounds(bounds); // Fit the map to show all route points, only once on load
      boundsFitRef.current = true; // Set ref to true after fitting bounds
    }
  }, [routePoints, map]);

  return null;
};

// Smooth interpolation function with easing for natural movement
const interpolatePosition = (start, end, progress) => {
  // Use ease-in-out for smoother acceleration/deceleration
  const easedProgress = progress < 0.5 
    ? 2 * progress * progress 
    : 1 - Math.pow(-2 * progress + 2, 2) / 2;
  
  const lat = start[0] + (end[0] - start[0]) * easedProgress;
  const lng = start[1] + (end[1] - start[1]) * easedProgress;
  return [lat, lng];
};

const LeafletMapHistoryVehicle = ({ mapHeight, routeData ,isMarkerShow , handleNewSearch}) => {
  const generalSettingLists = useSelector((state) => state.generalSettingReducer.generalSettingLists);
  const mapProvider = generalSettingLists?.mapProvider;
  const tileLayerConfig = getTileLayerConfig(mapProvider);
  const defaultMapView = useMemo(() => getDefaultMapView(generalSettingLists), [generalSettingLists]);
  const [play, setPlay] = useState(true);
  const [progress, setProgress] = useState(0); // Represents current position on the route
  const [interpolatedPosition, setInterpolatedPosition] = useState([0, 0]); // For smooth position updates
  const [step, setStep] = useState(0); // Save the current step for interpolation
  const [speed, setSpeed] = useState(1); // Speed multiplier (1x, 2x, 4x, 8x)
  const intervalRef = useRef(null); // To store the setInterval reference
  const animationFrameRef = useRef(null); // For smooth animation
  const playRef = useRef(play); // Ref to track play state
  const progressRef = useRef(progress); // Ref to track progress state
  const speedRef = useRef(speed); // Ref to track speed state
  const routeDataRef = useRef(routeData); // Ref to track routeData

  const totalPoints = routeData?.length || 0;

  // Initialize interpolated position when routeData changes
  useEffect(() => {
    if (routeData?.length > 0) {
      const currentIndex = Math.floor(progress);
      const initialPos = [
        parseFloat(routeData[currentIndex]?.lat || routeData[0].lat),
        parseFloat(routeData[currentIndex]?.lng || routeData[0].lng)
      ];
      if (initialPos[0] !== 0 || initialPos[1] !== 0) {
        setInterpolatedPosition(initialPos);
      }
    }
  }, [routeData, progress]);

  // Get current vehicle data
  const currentVehicleData = routeData?.[Math.floor(progress)] || routeData?.[progress] || {};
  // Try to get vehicleType from first data point or current point
  const vehicleType = routeData?.[0]?.vehicleType || currentVehicleData.vehicleType || 'Tractor';

  // Update refs when state changes
  useEffect(() => {
    playRef.current = play;
  }, [play]);

  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  useEffect(() => {
    speedRef.current = speed;
  }, [speed]);

  useEffect(() => {
    routeDataRef.current = routeData;
  }, [routeData]);

  // Function to stop animation
  const stopAnimation = () => {
    clearInterval(intervalRef.current);
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  // Function to start animation
  const startAnimation = () => {
    stopAnimation(); // Clear any existing animation first
    
    if (!routeDataRef.current || routeDataRef.current.length === 0) {
      return;
    }

    let lastTime = Date.now();
    const routePoints = routeDataRef.current.map((location) => [
      parseFloat(location.lat),
      parseFloat(location.lng),
    ]);
    
    const animate = () => {
      const now = Date.now();
      const deltaTime = Math.min(now - lastTime, 100); // Cap deltaTime to prevent large jumps
      lastTime = now;
      
      // Use refs to get current values
      const currentPlay = playRef.current;
      const currentProgress = progressRef.current;
      const currentSpeed = speedRef.current;
      const currentTotalPoints = routeDataRef.current?.length || 0;
      
      if (currentPlay && currentProgress < currentTotalPoints - 1 && routePoints.length > 0) {
        // Calculate step increment based on speed (faster speed = larger increment)
        const baseInterval = 1000; // Base time between points in ms (increased for slower, more visible movement)
        const intervalTime = baseInterval / currentSpeed;
        const stepIncrement = Math.max((deltaTime / intervalTime) * 0.3, 0.005); // Reduced step increment for slower movement
        
      setStep((prevStep) => {
          const newStep = Math.min(prevStep + stepIncrement, 1);
          
        if (newStep >= 1) {
            // Move to next point
          setProgress((prevProgress) => {
              const nextProgress = Math.min(prevProgress + 1, currentTotalPoints - 1);
              progressRef.current = nextProgress;
              
              // Update position immediately to next point
              if (routePoints[nextProgress]) {
                setInterpolatedPosition(routePoints[nextProgress]);
              }
              
              if (nextProgress >= currentTotalPoints - 1) {
                playRef.current = false;
                setPlay(false);
                stopAnimation();
              }
            return nextProgress;
          });
            return 0;
        } else {
          // Update interpolated position for smoother movement
            if (routePoints[currentProgress] && routePoints[currentProgress + 1]) {
          const newInterpolatedPosition = interpolatePosition(
                routePoints[currentProgress],
                routePoints[currentProgress + 1],
            newStep
          );
          setInterpolatedPosition(newInterpolatedPosition);
            }
          return newStep;
        }
      });
        
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        // Stop animation if not playing or reached end
        stopAnimation();
      }
    };
    
    animationFrameRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    const routePoints = routeData?.map((location) => [
      parseFloat(location.lat),
      parseFloat(location.lng),
    ]);

    // When playing, start the animation
    if (play && progress < totalPoints - 1 && routePoints.length > 0) {
      startAnimation();
    } else {
      stopAnimation();
    }

    return () => {
      stopAnimation();
    };
  }, [play, progress, speed, totalPoints, routeData]);

  const handlePlayPause = () => {
    const newPlayState = !play;
    setPlay(newPlayState);
    playRef.current = newPlayState;
    
    // If starting, ensure we have valid data
    if (newPlayState && routeData?.length > 0 && progress < totalPoints - 1) {
      // Animation will start via useEffect
    } else {
      stopAnimation();
    }
  };

  const handleSliderChange = (e) => {
    const newValue = parseInt(e.target.value, 10);
    setProgress(newValue);
    setStep(0); // Reset step when manually adjusting the slider

    // Update position immediately when slider is moved
    const newInterpolatedPosition = [
      parseFloat(routeData[newValue].lat),
      parseFloat(routeData[newValue].lng),
    ];
    setInterpolatedPosition(newInterpolatedPosition);

    setPlay(false); // Stop playing when the user adjusts the slider
  };

  // Function to handle speed change
  const handleSpeedChange = (newSpeed) => {
    setSpeed(newSpeed);
    speedRef.current = newSpeed;
    // Animation will restart via useEffect when speed changes
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleTimeString(); // You can adjust this for different formats
  };

  // Get battery icon
  const getBatteryIcon = (level) => {
    const batteryLevel = parseFloat(level) || 0;
    if (batteryLevel >= 0.75) return <FaBatteryFull style={{ color: '#28a745' }} />;
    if (batteryLevel >= 0.5) return <FaBatteryThreeQuarters style={{ color: '#ffc107' }} />;
    if (batteryLevel >= 0.25) return <FaBatteryHalf style={{ color: '#fd7e14' }} />;
    if (batteryLevel > 0) return <FaBatteryQuarter style={{ color: '#dc3545' }} />;
    return <FaBatteryEmpty style={{ color: '#6c757d' }} />;
  };

  // Get current vehicle data for display
  const currentData = routeData?.[progress] || {};
  const progressPercentage = totalPoints > 0 ? ((progress + 1) / totalPoints) * 100 : 0;
  
  // Calculate cumulative distance (sum from start to current point)
  const cumulativeDistance = routeData?.slice(0, progress + 1).reduce((sum, point) => {
    return sum + (parseFloat(point.distanceFromLastMeters) || 0);
  }, 0) || 0;

  return (
    <div style={{ height: mapHeight, display: 'flex', flexDirection: 'column' }}>
      {/* Map Section - Top */}
      <div style={{ flex: 1, minHeight: 0 }}>
        <MapContainer center={defaultMapView.center} zoom={defaultMapView.zoom} style={{ height: '100%' }}>
        <TileLayer
          key={mapProvider}
          url={tileLayerConfig.url}
          subdomains={tileLayerConfig.subdomains}
          attribution={tileLayerConfig.attribution}
          maxZoom={tileLayerConfig.maxZoom}
        />
        {/* Fit the map bounds based on route points */}
        <FitMapBoundsOnLoad
          routePoints={routeData?.map((location) => [
            parseFloat(location.lat),
            parseFloat(location.lng),
          ])}
        />

        {/* Render the route using Polyline */}
        {routeData?.length > 0 && (
          <Polyline
            positions={routeData?.map((location) => [
              parseFloat(location.lat),
              parseFloat(location.lng),
            ])}
            color="blue"
          />
        )}
        {isMarkerShow && (<> 
            {routeData?.length > 0 && currentVehicleData && (
            <Marker
              position={
                  (interpolatedPosition[0] !== 0 || interpolatedPosition[1] !== 0) && 
                  !isNaN(interpolatedPosition[0]) && 
                  !isNaN(interpolatedPosition[1])
                    ? interpolatedPosition
                    : routeData[progress] 
                      ? [
                  parseFloat(routeData[progress].lat),
                  parseFloat(routeData[progress].lng),
                ]
                      : routeData[0]
                        ? [
                            parseFloat(routeData[0].lat),
                            parseFloat(routeData[0].lng),
                          ]
                        : [0, 0]
                }
                icon={createRotatedCarIcon(
                  currentVehicleData.heading || routeData[progress]?.heading,
                  vehicleType,
                  currentVehicleData.is_charging || currentVehicleData.isCharging,
                  currentVehicleData.speed || routeData[progress]?.speed
                )}
              >
                <Popup maxWidth={300} className="vehicle-popup">
                  <div style={{ minWidth: '250px', fontFamily: 'Arial, sans-serif' }}>
                    {/* Header */}
                    <div style={{ 
                      borderBottom: '2px solid #4fbaf6', 
                      paddingBottom: '8px', 
                      marginBottom: '12px' 
                    }}>
                      <h5 style={{ 
                        margin: 0, 
                        color: '#333', 
                        fontSize: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        {vehicleType?.toLowerCase() === 'tractor' ? (
                          <FaTruck style={{ color: '#4fbaf6' }} />
                        ) : (
                          <FaCar style={{ color: '#4fbaf6' }} />
                        )}
                        <strong>{currentVehicleData.vehicleNumber || routeData[progress]?.vehicleNumber || 'N/A'}</strong>
                      </h5>
                    </div>

                    {/* Vehicle Info Grid */}
                    <div style={{ display: 'grid', gap: '8px', fontSize: '13px' }}>
                      {/* Vehicle Type */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FaTruck style={{ color: '#6c757d', fontSize: '14px' }} />
                        <span style={{ fontWeight: '500' }}>Type:</span>
                        <span>{vehicleType || 'N/A'}</span>
                      </div>

                      {/* Activity Status */}
                      {(currentVehicleData.activity_type || routeData[progress]?.activity_type) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {getActivityIcon(currentVehicleData.activity_type || routeData[progress]?.activity_type)}
                          <span style={{ fontWeight: '500' }}>Status:</span>
                          <span style={{ 
                            textTransform: 'capitalize',
                            color: (currentVehicleData.activity_type || routeData[progress]?.activity_type)?.toLowerCase() === 'still' ? '#6c757d' : '#28a745'
                          }}>
                            {currentVehicleData.activity_type || routeData[progress]?.activity_type || 'N/A'}
                          </span>
                        </div>
                      )}

                      {/* Speed */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FaTachometerAlt style={{ color: '#4fbaf6', fontSize: '14px' }} />
                        <span style={{ fontWeight: '500' }}>Speed:</span>
                        <span>
                          {currentVehicleData.speed != null || routeData[progress]?.speed != null
                            ? `${parseFloat(currentVehicleData.speed || routeData[progress]?.speed || 0).toFixed(2)} km/h`
                            : '0.00 km/h'
                          }
                        </span>
                      </div>

                      {/* Battery Level */}
                      {(currentVehicleData.level != null || routeData[progress]?.level != null) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {getBatteryIcon(currentVehicleData.level || routeData[progress]?.level)}
                          <span style={{ fontWeight: '500' }}>Battery:</span>
                          <span>{Math.round(parseFloat(currentVehicleData.level || routeData[progress]?.level || 0) * 100)}%</span>
                          {(currentVehicleData.is_charging || currentVehicleData.isCharging || routeData[progress]?.is_charging || routeData[progress]?.isCharging) && (
                            <span style={{ 
                              display: 'inline-flex', 
                              alignItems: 'center', 
                              gap: '4px',
                              color: '#28a745',
                              fontSize: '12px',
                              marginLeft: '4px'
                            }}>
                              <FaPlug style={{ fontSize: '10px' }} />
                              Charging
                            </span>
                          )}
                        </div>
                      )}

                      {/* Accuracy */}
                      {(currentVehicleData.accuracy || routeData[progress]?.accuracy) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FaMapMarkerAlt style={{ color: '#fd7e14', fontSize: '14px' }} />
                          <span style={{ fontWeight: '500' }}>Accuracy:</span>
                          <span>{parseFloat(currentVehicleData.accuracy || routeData[progress]?.accuracy || 0).toFixed(2)} m</span>
                        </div>
                      )}

                      {/* Heading */}
                      {(currentVehicleData.heading != null || routeData[progress]?.heading != null) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FaExclamationCircle style={{ color: '#6c757d', fontSize: '14px' }} />
                          <span style={{ fontWeight: '500' }}>Heading:</span>
                          <span>{parseFloat(currentVehicleData.heading || routeData[progress]?.heading || 0).toFixed(2)}°</span>
                        </div>
                      )}

                      {/* Timestamp */}
                      {(currentVehicleData.timestamp || currentVehicleData.recordedAt || routeData[progress]?.timestamp || routeData[progress]?.recordedAt) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FaClock style={{ color: '#6c757d', fontSize: '14px' }} />
                          <span style={{ fontWeight: '500' }}>Time:</span>
                          <span style={{ fontSize: '12px' }}>
                            {getDisplayDateTimeFormat(currentVehicleData.timestamp || currentVehicleData.recordedAt || routeData[progress]?.timestamp || routeData[progress]?.recordedAt)}
                          </span>
                        </div>
                      )}

                      {/* Address */}
                      {(currentVehicleData.address || routeData[progress]?.address) && (
                        <div style={{ 
                          marginTop: '8px', 
                          paddingTop: '8px', 
                          borderTop: '1px solid #e0e0e0',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '8px'
                        }}>
                          <FaMapMarkerAlt style={{ color: '#6c757d', fontSize: '14px', marginTop: '2px' }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '500', marginBottom: '4px' }}>Address:</div>
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              {currentVehicleData.address || routeData[progress]?.address}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Distance from last */}
                      {(currentVehicleData.distanceFromLastMeters || routeData[progress]?.distanceFromLastMeters) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FaExclamationCircle style={{ color: '#6c757d', fontSize: '14px' }} />
                          <span style={{ fontWeight: '500' }}>Distance:</span>
                          <span>{parseFloat(currentVehicleData.distanceFromLastMeters || routeData[progress]?.distanceFromLastMeters || 0).toFixed(2)} m</span>
                        </div>
                      )}

                      {/* Internet Status */}
                      {(currentVehicleData.internetStatus || routeData[progress]?.internetStatus) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FaCheckCircle style={{ 
                            color: (currentVehicleData.internetStatus || routeData[progress]?.internetStatus) === 'online' ? '#28a745' : '#dc3545',
                            fontSize: '14px' 
                          }} />
                          <span style={{ fontWeight: '500' }}>Internet:</span>
                          <span style={{ 
                            textTransform: 'capitalize',
                            color: (currentVehicleData.internetStatus || routeData[progress]?.internetStatus) === 'online' ? '#28a745' : '#dc3545'
                          }}>
                            {currentVehicleData.internetStatus || routeData[progress]?.internetStatus}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </Popup>
              </Marker>
          )}
        </>)}
        </MapContainer>
      </div>

      {/* Info Card Section - Bottom */}
      {isMarkerShow && (
        <Card style={{ marginTop: '8px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', flexShrink: 0 }}>
          <Card.Body style={{ padding: '10px' }}>
            {/* Single Line Controls: Play/Pause, New Search, Progress Bar, Speed Icons */}
            <div style={{ marginBottom: '8px' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: '8px',
                marginBottom: '5px'
              }}>
                {/* Play/Pause Button */}
                <button
                  type="button"
                  onClick={handlePlayPause}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    border: 'none',
                    borderRadius: '50%',
                    backgroundColor: play ? '#dc3545' : '#28a745',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '12px',
                    transition: 'all 0.3s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    flexShrink: 0
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'scale(1.1)';
                    e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.2)';
                  }}
                  title={play ? 'Stop' : 'Play'}
                >
                  {play ? <FaStop /> : <FaPlay />}
                </button>

                {/* New Search Button */}
                <button
                  type="button"
                  onClick={handleNewSearch}
          style={{
            display: 'flex',
            alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    border: 'none',
                    borderRadius: '50%',
                    backgroundColor: '#4fbaf6',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '12px',
                    transition: 'all 0.3s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    flexShrink: 0
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'scale(1.1)';
                    e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.2)';
                  }}
                  title="New Search"
                >
                  <FaSearch />
                </button>

                {/* Progress Text */}
                <span style={{ fontSize: '10px', color: '#666', fontWeight: '500', minWidth: '70px' }}>
                  {progress + 1} / {totalPoints}
                </span>

                {/* Progress Bar */}
                <div style={{
                  flex: 1,
                  height: '8px',
                  backgroundColor: '#e9ecef',
                  borderRadius: '6px',
                  overflow: 'hidden',
                  boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)',
                  position: 'relative'
                }}>
                  <div
                    style={{
                      width: `${progressPercentage}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #4fbaf6 0%, #1872e4 100%)',
                      borderRadius: '10px',
                      transition: 'width 0.3s ease',
                      boxShadow: '0 2px 4px rgba(79, 186, 246, 0.4)',
                      position: 'relative'
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      right: '0',
                      top: '0',
                      width: '4px',
                      height: '100%',
                      backgroundColor: 'white',
                      boxShadow: '-2px 0 4px rgba(0,0,0,0.2)'
                    }} />
            </div>
          </div>

                {/* Progress Percentage */}
                <span style={{ fontSize: '10px', color: '#666', fontWeight: '500', minWidth: '40px' }}>
                  {progressPercentage.toFixed(1)}%
                </span>

                {/* Speed Icons */}
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexShrink: 0 }}>
                  {[1, 2, 4, 8, 16].map((spd) => (
                    <button
                      key={spd}
                      onClick={() => handleSpeedChange(spd)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '28px',
                        height: '28px',
                        border: speed === spd ? '2px solid #4fbaf6' : '1px solid #ddd',
                        borderRadius: '50%',
                        backgroundColor: speed === spd ? '#4fbaf6' : 'white',
                        color: speed === spd ? 'white' : '#333',
                        cursor: 'pointer',
                        fontSize: '9px',
                        fontWeight: speed === spd ? '600' : '400',
                        transition: 'all 0.2s',
                        boxShadow: speed === spd ? '0 1px 3px rgba(79, 186, 246, 0.3)' : 'none'
                      }}
                      onMouseEnter={(e) => {
                        if (speed !== spd) {
                          e.target.style.backgroundColor = '#f0f0f0';
                          e.target.style.transform = 'scale(1.1)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (speed !== spd) {
                          e.target.style.backgroundColor = 'white';
                          e.target.style.transform = 'scale(1)';
                        }
                      }}
                      title={`${spd}X Speed`}
                    >
                      {spd === 1 ? '1' : spd === 2 ? '2' : spd === 4 ? '4' : spd === 8 ? '8' : '16'}
                    </button>
                  ))}
          </div>
        </div>
              {/* Slider */}
          <input
            type="range"
            min="0"
            max={totalPoints - 1}
            value={progress}
            onChange={handleSliderChange}
                style={{
                  width: '100%',
                  height: '6px',
                  marginTop: '3px',
                  cursor: 'pointer',
                  appearance: 'none',
                  background: 'transparent'
                }}
          />
        </div>

            {/* Vehicle Details Row - Single Line - Icons Only */}
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '8px', 
              borderRadius: '6px',
              marginTop: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              flexWrap: 'nowrap'
            }}>
              {/* Battery */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
                <div style={{ fontSize: '16px' }}>{getBatteryIcon(currentData.level)}</div>
                <span style={{ fontSize: '12px', fontWeight: '600' }}>
                  {Math.round(parseFloat(currentData.level || 0) * 100)}%
                  {currentData.is_charging && (
                    <FaPlug style={{ marginLeft: '3px', color: '#28a745', fontSize: '10px' }} />
                  )}
                </span>
              </div>

              {/* Speed */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
                <FaTachometerAlt style={{ color: '#4fbaf6', fontSize: '16px' }} />
                <span style={{ fontSize: '12px', fontWeight: '600' }}>
                  {parseFloat(currentData.speed || 0).toFixed(1)} km/h
                </span>
              </div>

              {/* Total Distance */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
                <FaRoute style={{ color: '#4fbaf6', fontSize: '16px' }} />
                <span style={{ fontSize: '12px', fontWeight: '600' }}>
                  {cumulativeDistance > 1000 
                    ? `${(cumulativeDistance / 1000).toFixed(2)} km`
                    : `${cumulativeDistance.toFixed(0)} m`
                  }
                </span>
              </div>

              {/* Vehicle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
                <FaMapMarkerAlt style={{ color: '#6c757d', fontSize: '16px' }} />
                <span style={{ fontSize: '12px', fontWeight: '600' }}>
                  {currentData.vehicleNumber || 'N/A'}
                </span>
              </div>

              {/* Timestamp */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
                <FaClock style={{ color: '#6c757d', fontSize: '16px' }} />
                <span style={{ fontSize: '12px', fontWeight: '600' }}>
                  {formatTimestamp(currentData.timestamp)}
                </span>
              </div>

              {/* Address */}
              {(currentData.address || routeData[progress]?.address) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0, flex: 1, minWidth: 0 }}>
                  <FaMapMarkerAlt style={{ color: '#fd7e14', fontSize: '16px' }} />
                  <span style={{ 
                    fontSize: '12px', 
                    fontWeight: '600',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {currentData.address || routeData[progress]?.address || 'N/A'}
                  </span>
                </div>
              )}
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default LeafletMapHistoryVehicle;
